import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    this.style.removeProperty('left');
    this.style.removeProperty('top');

    const isMobileCalmi = window.innerWidth < 750 && Boolean(this.closest('.facets--horizontal_calmi'));
    if (isMobileCalmi) {
      const padding = 16;
      const rect = this.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      let shift = 0;

      if (rect.right > viewportWidth - padding) {
        shift -= rect.right - (viewportWidth - padding);
      }

      if (rect.left + shift < padding) {
        shift += padding - (rect.left + shift);
      }

      if (shift !== 0) {
        this.style.left = `${shift}px`;
      }

      this.#mutationObserver.takeRecords();
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    const nextRect = this.getBoundingClientRect();
    if (nextRect.left < 0) {
      const currentLeft = Number.parseFloat(this.style.left) || 0;
      this.style.left = `${currentLeft + Math.abs(nextRect.left) + OFFSET}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
